/**
 * Stream B — B66-B68: CRM Enrichment Hooks
 *
 * Real apiFetch to /crm/intelligence/contacts/:id/enrichment.
 * No mocks. Supports LinkedIn, firmographics, AI web enrichment.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

export interface EnrichmentSource {
  source: string;
  label: string;
  status: "found" | "not_found" | "error" | "pending";
  fieldsFound: string[];
  confidence: number;
  enrichedAt: string | null;
  responseTimeMs: number | null;
}

export interface EnrichmentData {
  enrichedAt: string | null;
  sources: EnrichmentSource[];
  fields: Record<string, {
    value: unknown;
    source: string;
    confidence: number;
    enrichedAt: string;
  }>;
}

export function useCrmEnrichment(contactId: string | null) {
  const [data, setData] = useState<EnrichmentData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEnrichment = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: EnrichmentData }>(
        `/crm/intelligence/contacts/${contactId}/enrichment`,
      );
      setData(res.data);
    } catch {
      setData(null);
      setError("Failed to load enrichment data");
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  useEffect(() => { fetchEnrichment(); }, [fetchEnrichment]);

  return { data, isLoading, error, refetch: fetchEnrichment };
}

export function useCrmEnrich(contactId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enrich = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: { enrichedAt: string } }>(
        `/crm/intelligence/contacts/${contactId}/enrich`,
        { method: "POST" },
      );
      return res.data;
    } catch {
      setError("Failed to enrich contact");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  return { enrich, isLoading, error };
}
